import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { AUTH_COOKIE_NAME, taco_cookie_ctx } from "@/auth-cookie";
import { logRejection } from "@/helpers";
import { useRequest, type ResponseData } from "@/hooks/request";
import ApiError from "@/models/ApiError";
import type { User } from "@/types/user";

/** `LoginReturn` from the API; error responses carry `detail` instead. */
interface LoginResponse {
  token: string;
  user: User;
  detail?: string;
}

/** `JwtClaims` from `/api/auth/session`. */
interface SessionResponse {
  user: User;
  magic_token?: string;
  exp?: number;
  iss?: string;
}

/** `UserToken` from `/api/auth/create-user-link/{email}`. */
interface UserTokenResponse {
  user_email: string;
  expires: string;
  token: string;
}

interface AuthContextType {
  user: User | null;
  error: boolean | string;
  is_loading: boolean;
  setLoading: (loading: boolean) => void;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  tokenLogin: (token: string, recaptcha_token: string) => Promise<void>;
  updateUser: (
    name: string,
    title: string,
    // The account's current photo travels back as its URL and is left alone.
    // Only a File is uploaded, which is what updateUser's instanceof check sorts.
    photo: File | string | null,
    clear_image: boolean,
    new_password?: string,
  ) => Promise<boolean>;
  updateUserPassword: (
    new_password: string,
    current_password: string,
  ) => Promise<ResponseData<LoginResponse>>;
  getCreateUserLink: (user_email: string) => Promise<{
    data: UserTokenResponse | null;
    error: ApiError | null;
  }>;
  createUserTokenValidation: (token: string, recaptcha_token: string) => Promise<void>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<boolean | string>(false);
  // Without a cookie there is no session to fetch, so the provider is not
  // loading and the mount effect has nothing to announce.
  const [is_loading, setLoading] = useState(
    () => taco_cookie_ctx.get(AUTH_COOKIE_NAME) !== undefined,
  );
  // One controller for the provider's lifetime. It used to be rebuilt on every
  // render, so the cleanup below aborted the first render's controller while
  // every live request carried a later one.
  const [abort_controller] = useState(() => new AbortController());
  const request = useRequest(abort_controller.signal);

  useEffect(() => {
    if (taco_cookie_ctx.get(AUTH_COOKIE_NAME)) {
      // Restoring a session is a fetch against the API, which is the external
      // system an effect is for. The state lands after the await, not during it.
      const getSession = async () => {
        const { json, is_ok } = await request<SessionResponse>("/api/auth/session", {});
        setUser(is_ok ? json.user : null);
        setLoading(false);
      };
      getSession().catch(logRejection);
    }
    return () => {
      abort_controller.abort();
    };
  }, [abort_controller, request]);

  const signIn = useCallback(async (username: string, password: string) => {
    setLoading(true);
    const response = await fetch("/api/auth/login", {
      method: "post",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    try {
      const json = (await response.json()) as LoginResponse;
      if (response.ok) {
        // reset error
        setError(false);
        // write jwt to cookie
        taco_cookie_ctx.set(AUTH_COOKIE_NAME, json.token);
        // and set userdata as state
        setUser(json.user);
        setLoading(false);
      } else {
        setError(json.detail ?? true);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, []);

  const getCreateUserLink = useCallback(
    (user_email: string) => {
      return request<UserTokenResponse>(`/api/auth/create-user-link/${user_email}`, {}).then(
        (response) => {
          if (response.is_ok) {
            return { data: response.json, error: null };
          } else {
            return { data: null, error: new ApiError(response) };
          }
        },
      );
    },
    [request],
  );

  const tokenLogin = useCallback(async (token: string, recaptcha_token: string) => {
    setLoading(true);
    const response = await fetch("/api/auth/token-login", {
      method: "post",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token, recaptcha_token }),
    });
    try {
      const json = (await response.json()) as LoginResponse;
      if (response.ok) {
        // reset error
        setError(false);
        // write jwt to cookie
        taco_cookie_ctx.set(AUTH_COOKIE_NAME, json.token);
        // and set userdata as state
        setUser(json.user);
      } else {
        setError(json.detail ?? true);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }, []);

  const createUserTokenValidation = useCallback(async (token: string, recaptcha_token: string) => {
    setLoading(true);
    const response = await fetch("/api/auth/create-user-token-validation", {
      method: "post",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token, recaptcha_token }),
    });
    try {
      const json = (await response.json()) as LoginResponse;
      if (response.ok) {
        // reset error
        setError(false);
        // write jwt to cookie
        taco_cookie_ctx.set(AUTH_COOKIE_NAME, json.token);
        // and set userdata as state
        setUser(json.user);
      } else {
        setError(json.detail ?? true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    taco_cookie_ctx.remove(AUTH_COOKIE_NAME);
    setLoading(false);
    setUser(null);
  }, []);

  const updateUserPassword = useCallback(
    async (new_password: string, current_password: string) => {
      const response = await request<LoginResponse>("/api/auth/user/pwd", {
        method: "put",
        body: JSON.stringify({ new_password, current_password }),
      });
      try {
        if (response.is_ok) {
          setError(false);
          taco_cookie_ctx.set(AUTH_COOKIE_NAME, response.json.token);
          setUser(response.json.user);
        } else {
          setError(response.json.detail ?? true);
        }
        return response;
      } catch (err) {
        console.error(err);
        setError(err as string);
        return response;
      }
    },
    [request],
  );

  const updateUser = useCallback(
    async (
      name: string,
      title: string,
      photo: File | string | null,
      clear_image: boolean,
      new_password = "",
    ) => {
      if (!user) return false;
      setLoading(true);
      const form_data = new FormData();
      form_data.append("name", name);
      form_data.append("title", title);
      if (photo && photo instanceof File) {
        form_data.append("photo", photo);
      }
      form_data.append("clear_image", String(clear_image));
      form_data.append("new_password", new_password);

      const response = await request<LoginResponse>(
        "/api/auth/user",
        {
          method: "put",
          body: form_data,
        },
        false,
      );
      if (response.is_ok) {
        // reset error
        setError(false);
        // write jwt to cookie
        taco_cookie_ctx.set(AUTH_COOKIE_NAME, response.json.token);
        // and set userdata as state
        setUser(response.json.user);
        setLoading(false);
        return true;
      } else {
        setError(response.json.detail ?? true);
        setLoading(false);
        return false;
      }
    },
    [request, user],
  );

  const value = useMemo(
    () => ({
      user,
      error,
      is_loading,
      setLoading,
      signIn,
      signOut,
      tokenLogin,
      updateUser,
      updateUserPassword,
      getCreateUserLink,
      createUserTokenValidation,
    }),
    [
      user,
      error,
      is_loading,
      signIn,
      signOut,
      tokenLogin,
      updateUser,
      updateUserPassword,
      getCreateUserLink,
      createUserTokenValidation,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used with AuthProvider");
  }
  return context;
};
